import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import { useAuthStore } from "@user/store/auth_store";

import PrintTrtag, { type PrintTrtagHandle } from "@user/views/print/print_trtag";

import plusIcon from "@user/assets/images/common/plus-secondary.svg";
import minusIcon from "@user/assets/images/common/minus-secondary.svg";

interface State {
  infos: any[];
}

const defState: State = {
  infos: [],
};

function PopupPrintTrtag(props: any) {
  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const printTrtagRef = useRef<PrintTrtagHandle>(null);

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    setState({
      infos: props.data.infos.map((item: any) => ({ ...item, printQty: "1" })),
    });
  }

  async function closePopup() {
    props.onClose();
  }

  async function updatePrintQty(idx: number, nextQty: string) {
    setState((prev) => ({
      ...prev,
      infos: prev.infos.map((item, i) =>
        i === idx ? { ...item, printQty: nextQty } : item
      ),
    }));
  }

  const pdfUrls = state.infos.flatMap((info) => {
    const qty = Math.max(Number(info.printQty) || 0, 0);
    return Array.from({ length: qty }, () => "/api/inoutbound/inout_work.download?mode=downloadTrFile&workReqErpNo=" + encodeURIComponent(info.workReqErpNo));
  });

  async function doPrint() {
    await printTrtagRef.current?.print(pdfUrls);
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "1100px" } } }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          TR Tag 출력
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* TR Tag 출력 */}
            <div className="table-wrap">
              <table className="tbl-list">
                <colgroup>
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col" className="ta-left">P/N</th>
                    <th scope="col" className="ta-left">TR No</th>
                    <th scope="col" className="ta-left">S/N</th>
                    <th scope="col">KWD</th>
                    <th scope="col">CTG</th>
                    <th scope="col" className="ta-right">Request Qty</th>
                    <th scope="col">UOM</th>
                    <th scope="col">출력매수</th>
                  </tr>
                </thead>
                <tbody>
                  {state.infos.map((info, idx) => (
                    <tr key={idx}>
                      <td className="ta-left">{info.pn}</td>
                      <td className="ta-left">{info.trNo}</td>
                      <td className="ta-left">{info.sn}</td>
                      <td>{info.kwd}</td>
                      <td>{info.ctg}</td>
                      <td className="ta-right">{info.reqQty}</td>
                      <td>{info.uom}</td>
                      <td>
                        <div className="sg-item">
                          <div className="qty-stepper">
                            <button
                              type="button"
                              onClick={() =>
                                updatePrintQty(
                                  idx,
                                  Number(info.printQty) < 2 ? "1" : String(Number(info.printQty) - 1)
                                )
                              }
                            >
                              <img src={minusIcon} alt="감소" />
                            </button>
                            <input
                              type="text"
                              value={info.printQty}
                              onChange={(e) => updatePrintQty(idx, e.target.value)}
                              readOnly
                            />
                            <button
                              type="button"
                              onClick={() => updatePrintQty(idx, String(Number(info.printQty) + 1))}
                            >
                              <img src={plusIcon} alt="증가" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/*// TR Tag 출력 */}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
          {auth.download && (
            <Button variant="contained" autoFocus onClick={doPrint}>
              출력
            </Button>
          )}
        </DialogActions>
        <PrintTrtag ref={printTrtagRef} />
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupPrintTrtag);